import { Fragment, useState, useMemo, useRef } from 'react';
import AddIcon from '@mui/icons-material/Add';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import WarningIcon from '@mui/icons-material/Warning';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Collapse from '@mui/material/Collapse';
import FormControl from '@mui/material/FormControl';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import ErrorBox from '../../core/components/ErrorBox';
import PageContainer from '../../core/components/PageContainer';
import { formatDateForLocale } from '../../core/dateFormatting';
import { usePropertyId } from '../../core/hooks/usePropertyId';
import PropertySelectionDialog from '../../property/components/PropertySelectionDialog';
import useProperty from '../../property/hooks/useProperty';
import useTaxPeriods, { type TaxPeriod, type TaxPeriodFilters } from '../hooks/useTaxPeriods';
import useTaxPayments from '../hooks/useTaxPayments';
import RecordPaymentDialog from './RecordPaymentDialog';
import GeneratePeriodsDialog from './GeneratePeriodsDialog';
import TourismTaxBreakdownPanel from './TourismTaxBreakdownPanel';
import UploadInvoicesDialog from './UploadInvoicesDialog';

const formatCurrency = (amount: number | null, currency: string) => {
    if (amount === null) return '-';
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(amount);
};

const formatPeriod = (period: TaxPeriod) => {
    const start = new Date(period.periodStart);

    if (period.periodType === 'yearly') {
        return start.getFullYear().toString();
    } else if (period.periodType === 'monthly') {
        return start.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
    } else {
        const quarter = Math.floor(start.getMonth() / 3) + 1;
        return `Q${quarter} ${start.getFullYear()}`;
    }
};

const getComponentDisplayName = (componentId: string): string => {
    const names: Record<string, string> = {
        'tourism-tax': 'Tourism Tax',
        'tourism-dev': 'Tourism Dev.',
        'szja': 'SZJA',
        'szocho': 'SZOCHO',
        'tb': 'TB',
        'ipa': 'IPA',
    };
    return names[componentId] ?? componentId.toUpperCase();
};

const selectStyles = {
    '& .MuiOutlinedInput-root': {
        borderRadius: 0,
        backgroundColor: '#0f0f23',
        '& fieldset': {
            borderWidth: '2px',
            borderColor: '#374151',
        },
        '&:hover fieldset': {
            borderWidth: '2px',
            borderColor: '#6366f1',
        },
        '&.Mui-focused fieldset': {
            borderWidth: '3px',
            borderColor: '#6366f1',
        },
    },
    '& .MuiInputLabel-root': {
        fontWeight: 700,
        textTransform: 'uppercase',
        fontSize: '0.75rem',
        letterSpacing: '0.05em',
    },
};

const abbreviateAmount = (amount: number): string => {
    if (amount >= 1_000_000) return `${Math.round(amount / 1_000_000)}M`;
    if (amount >= 1_000) return `${Math.round(amount / 1_000)}k`;
    return amount.toString();
};

const StatusChip = ({ status, targetAmount, totalPaid }: { status: TaxPeriod['status']; targetAmount?: number | null; totalPaid?: number }) => {
    const config = {
        pending: { label: 'Pending', color: '#6b7280', icon: null },
        paid: { label: 'Paid', color: '#10b981', icon: <CheckCircleIcon sx={{ fontSize: 16 }} /> },
        partial: { label: 'Partial', color: '#f59e0b', icon: <WarningIcon sx={{ fontSize: 16 }} /> },
        overdue: { label: 'Overdue', color: '#ef4444', icon: <ErrorIcon sx={{ fontSize: 16 }} /> },
    };

    const { color, icon } = config[status];

    let label = config[status].label;
    if (status === 'partial' && targetAmount && totalPaid !== undefined) {
        label = `Partial ${abbreviateAmount(totalPaid)}/${abbreviateAmount(targetAmount)}`;
    }

    return (
        <Chip
            label={label}
            icon={icon ?? undefined}
            size="small"
            sx={{
                backgroundColor: `${color}20`,
                color,
                border: `2px solid ${color}`,
                borderRadius: 0,
                fontWeight: 700,
                textTransform: 'uppercase',
                fontSize: '0.625rem',
                letterSpacing: '0.05em',
            }}
        />
    );
};

const TargetAmountCell = ({ period, onUpdate, readonly }: { period: TaxPeriod; onUpdate: (periodId: number, amount: number | null) => Promise<void>; readonly?: boolean }) => {
    const [editing, setEditing] = useState(false);
    const [value, setValue] = useState('');
    const inputRef = useRef<HTMLInputElement>(null);

    const handleStartEdit = () => {
        setValue(period.targetAmount?.toString() ?? '');
        setEditing(true);
        setTimeout(() => inputRef.current?.focus(), 0);
    };

    const handleSave = async () => {
        setEditing(false);
        const parsed = value.trim() === '' ? null : parseFloat(value);
        if (parsed === (period.targetAmount ?? null)) return;
        if (parsed !== null && (isNaN(parsed) || parsed < 0)) return;
        await onUpdate(period.id, parsed);
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            handleSave();
        } else if (e.key === 'Escape') {
            setEditing(false);
        }
    };

    if (readonly) {
        return (
            <Box sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                {period.targetAmount != null ? formatCurrency(period.targetAmount, period.currency) : '-'}
            </Box>
        );
    }

    if (editing) {
        return (
            <input
                ref={inputRef}
                type="number"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                onBlur={handleSave}
                onKeyDown={handleKeyDown}
                min={0}
                style={{
                    width: '100%',
                    backgroundColor: '#1a1a2e',
                    color: '#fff',
                    border: '2px solid #6366f1',
                    padding: '4px 8px',
                    fontFamily: 'monospace',
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    outline: 'none',
                }}
            />
        );
    }

    if (period.targetAmount == null) {
        return (
            <Box
                onClick={handleStartEdit}
                sx={{
                    cursor: 'pointer',
                    color: '#6b7280',
                    fontStyle: 'italic',
                    fontSize: '0.8rem',
                    '&:hover': { color: '#6366f1' },
                }}
            >
                Set target...
            </Box>
        );
    }

    return (
        <Box
            onClick={handleStartEdit}
            sx={{
                cursor: 'pointer',
                fontFamily: 'monospace',
                fontWeight: 600,
                '&:hover': { color: '#6366f1' },
            }}
        >
            {formatCurrency(period.targetAmount, period.currency)}
        </Box>
    );
};

const TaxPaymentsPage = () => {
    const propertyId = usePropertyId();
    const pathname = window.location.hash.replace('#', '');

    const isLegacyRoute = pathname === '/tax-payments';
    const isPropertyRoute = /^\/\d+\/financial\/tax-payments/.test(pathname);

    const { property, isTourismTaxEnabled, isSzamlazzEnabled } = useProperty();

    const currentYear = new Date().getFullYear();
    const [filters, setFilters] = useState<TaxPeriodFilters>({ year: currentYear });

    const {
        periods,
        loading,
        error,
        refetch,
        generatePeriods,
        regeneratePeriods,
        updateTargetAmount,
    } = useTaxPeriods(propertyId, filters);

    const { createPayment, deletePayment } = useTaxPayments(propertyId);

    const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
    const [generateDialogOpen, setGenerateDialogOpen] = useState(false);
    const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
    const [selectedPeriod, setSelectedPeriod] = useState<TaxPeriod | null>(null);
    const [expandedPeriodId, setExpandedPeriodId] = useState<number | null>(null);

    const tourismTaxEnabled = isTourismTaxEnabled();
    const szamlazzEnabled = isSzamlazzEnabled();

    // Group periods by component
    const groupedPeriods = useMemo(() => {
        const groups = new Map<string, TaxPeriod[]>();
        for (const period of periods) {
            const existing = groups.get(period.componentId) ?? [];
            existing.push(period);
            groups.set(period.componentId, existing);
        }
        return groups;
    }, [periods]);

    // Determine generate/regenerate button state
    const hasPayments = useMemo(() => periods.some((p) => p.paymentsCount > 0), [periods]);
    const generateMode: 'generate' | 'regenerate' | 'hidden' = useMemo(() => {
        if (periods.length === 0) return 'generate';
        if (hasPayments) return 'hidden';
        return 'regenerate';
    }, [periods, hasPayments]);

    const handleOpenPaymentDialog = (period: TaxPeriod) => {
        setSelectedPeriod(period);
        setPaymentDialogOpen(true);
    };

    const handleClosePaymentDialog = () => {
        setPaymentDialogOpen(false);
        setSelectedPeriod(null);
    };

    const handleGeneratePeriods = async (year: number) => {
        if (generateMode === 'regenerate') {
            await regeneratePeriods(year);
        } else {
            await generatePeriods(year);
        }
        setGenerateDialogOpen(false);
    };

    const availableYears = useMemo(() => {
        const years: number[] = [];
        for (let y = currentYear - 2; y <= currentYear + 1; y++) {
            years.push(y);
        }
        return years;
    }, [currentYear]);

    const uniqueComponents = useMemo(() => {
        const components = new Set<string>();
        for (const period of periods) {
            components.add(period.componentId);
        }
        return Array.from(components);
    }, [periods]);

    if (!propertyId) {
        if (isPropertyRoute) {
            return null;
        }
        if (isLegacyRoute) {
            return <PropertySelectionDialog open={true} targetPath="/financial/tax-payments" />;
        }
        return null;
    }

    if (error) {
        return <ErrorBox error={new Error(error)} />;
    }

    const hasNoPreset = !property?.taxPresetId;

    return (
        <PageContainer>
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 4,
                    flexWrap: 'wrap',
                    gap: 2,
                }}
            >
                <Typography
                    variant="h2"
                    component="h1"
                    sx={{
                        fontSize: '2rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        fontWeight: 900,
                    }}
                >
                    Tax Payments
                </Typography>

                <Stack direction="row" spacing={2}>
                    {szamlazzEnabled && (
                        <Button
                            variant="outlined"
                            startIcon={<UploadFileIcon />}
                            onClick={() => setUploadDialogOpen(true)}
                            sx={{
                                borderRadius: 0,
                                borderWidth: '2px',
                                borderColor: '#374151',
                                fontWeight: 700,
                                textTransform: 'uppercase',
                                letterSpacing: '0.05em',
                                fontSize: '0.75rem',
                                '&:hover': {
                                    borderWidth: '2px',
                                    borderColor: '#6366f1',
                                },
                            }}
                        >
                            Upload Invoices
                        </Button>
                    )}
                    {generateMode !== 'hidden' && (
                        <Button
                            variant="outlined"
                            startIcon={<AutorenewIcon />}
                            onClick={() => setGenerateDialogOpen(true)}
                            disabled={hasNoPreset}
                            sx={{
                                borderRadius: 0,
                                borderWidth: '2px',
                                borderColor: '#374151',
                                fontWeight: 700,
                                textTransform: 'uppercase',
                                letterSpacing: '0.05em',
                                fontSize: '0.75rem',
                                '&:hover': {
                                    borderWidth: '2px',
                                    borderColor: '#6366f1',
                                },
                            }}
                        >
                            {generateMode === 'regenerate' ? 'Regenerate Periods' : 'Generate Periods'}
                        </Button>
                    )}
                </Stack>
            </Box>

            {hasNoPreset ? (
                <Box
                    sx={{
                        p: 4,
                        border: '3px solid #f59e0b',
                        backgroundColor: 'rgba(245, 158, 11, 0.1)',
                        textAlign: 'center',
                    }}
                >
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                        Tax Preset Not Configured
                    </Typography>
                    <Typography color="text.secondary" sx={{ mb: 2 }}>
                        Please configure a tax preset in the property settings to track tax payments.
                    </Typography>
                    <Button
                        variant="contained"
                        href={`#/${propertyId}/settings`}
                        sx={{
                            borderRadius: 0,
                            fontWeight: 700,
                            textTransform: 'uppercase',
                        }}
                    >
                        Go to Settings
                    </Button>
                </Box>
            ) : (
                <>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 4 }} alignItems="center">
                        <FormControl size="small" sx={{ ...selectStyles, minWidth: 120 }}>
                            <InputLabel id="year-filter-label">Year</InputLabel>
                            <Select
                                labelId="year-filter-label"
                                value={filters.year ?? ''}
                                onChange={(e) => {
                                    const value = e.target.value as number | '';
                                    setFilters({ ...filters, year: value === '' ? undefined : value });
                                }}
                                label="Year"
                            >
                                <MenuItem value="">All Years</MenuItem>
                                {availableYears.map((year) => (
                                    <MenuItem key={year} value={year}>
                                        {year}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl size="small" sx={{ ...selectStyles, minWidth: 140 }}>
                            <InputLabel id="status-filter-label">Status</InputLabel>
                            <Select
                                labelId="status-filter-label"
                                value={filters.status ?? ''}
                                onChange={(e) => {
                                    const value = e.target.value as '' | 'pending' | 'paid' | 'partial' | 'overdue';
                                    setFilters({ ...filters, status: value === '' ? undefined : value });
                                }}
                                label="Status"
                            >
                                <MenuItem value="">All Statuses</MenuItem>
                                <MenuItem value="pending">Pending</MenuItem>
                                <MenuItem value="paid">Paid</MenuItem>
                                <MenuItem value="partial">Partial</MenuItem>
                                <MenuItem value="overdue">Overdue</MenuItem>
                            </Select>
                        </FormControl>

                        <FormControl size="small" sx={{ ...selectStyles, minWidth: 160 }}>
                            <InputLabel id="component-filter-label">Component</InputLabel>
                            <Select
                                labelId="component-filter-label"
                                value={filters.componentId ?? ''}
                                onChange={(e) => {
                                    const value = e.target.value as string;
                                    setFilters({ ...filters, componentId: value === '' ? undefined : value });
                                }}
                                label="Component"
                            >
                                <MenuItem value="">All Components</MenuItem>
                                {uniqueComponents.map((id) => (
                                    <MenuItem key={id} value={id}>
                                        {getComponentDisplayName(id)}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Stack>

                    {loading ? (
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                py: 8,
                            }}
                        >
                            <CircularProgress />
                        </Box>
                    ) : periods.length === 0 ? (
                        <Box
                            sx={{
                                p: 4,
                                border: '3px solid #374151',
                                textAlign: 'center',
                            }}
                        >
                            <Typography color="text.secondary" sx={{ mb: 2 }}>
                                No tax periods found. Generate periods for a year to get started.
                            </Typography>
                            <Button
                                variant="contained"
                                onClick={() => setGenerateDialogOpen(true)}
                                sx={{
                                    borderRadius: 0,
                                    fontWeight: 700,
                                    textTransform: 'uppercase',
                                }}
                            >
                                Generate Periods
                            </Button>
                        </Box>
                    ) : (
                        <TableContainer
                            sx={{
                                border: '3px solid #374151',
                                borderRadius: 0,
                                backgroundColor: '#1a1a2e',
                            }}
                        >
                            <Table>
                                <TableHead>
                                    <TableRow
                                        sx={{
                                            backgroundColor: '#1a1a2e',
                                            borderBottom: '3px solid #6366f1',
                                        }}
                                    >
                                        {['Component', 'Period', 'Due Date', 'Target', 'Paid', 'Status', ''].map((header) => (
                                            <TableCell
                                                key={header}
                                                sx={{
                                                    fontWeight: 900,
                                                    textTransform: 'uppercase',
                                                    letterSpacing: '0.05em',
                                                    fontSize: '0.75rem',
                                                    color: 'text.secondary',
                                                }}
                                            >
                                                {header}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {Array.from(groupedPeriods.entries()).map(([componentId, componentPeriods]) => (
                                        componentPeriods.map((period, index) => {
                                            const isTourismTax = period.componentId === 'tourism-tax';
                                            const canExpand = isTourismTax && tourismTaxEnabled;
                                            const isExpanded = expandedPeriodId === period.id;
                                            const isReadonlyTarget = isTourismTax && tourismTaxEnabled;

                                            return (
                                                <Fragment key={period.id}>
                                                <TableRow
                                                    sx={{
                                                        borderBottom: isExpanded ? 'none' : '1px solid #374151',
                                                        transition: 'all 0.2s ease',
                                                        '&:hover': {
                                                            backgroundColor: 'rgba(99, 102, 241, 0.1)',
                                                        },
                                                        cursor: canExpand ? 'pointer' : undefined,
                                                    }}
                                                    onClick={canExpand ? () => setExpandedPeriodId(isExpanded ? null : period.id) : undefined}
                                                >
                                                    <TableCell
                                                        sx={{
                                                            fontWeight: 700,
                                                            color: index === 0 ? 'text.primary' : 'transparent',
                                                        }}
                                                    >
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                            {canExpand && (
                                                                <IconButton size="small" sx={{ p: 0 }}>
                                                                    {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                                                                </IconButton>
                                                            )}
                                                            {index === 0 ? getComponentDisplayName(componentId) : ''}
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell sx={{ fontWeight: 600 }}>
                                                        {formatPeriod(period)}
                                                    </TableCell>
                                                    <TableCell
                                                        sx={{
                                                            fontFamily: 'monospace',
                                                            fontWeight: 600,
                                                            color: period.status === 'overdue' ? '#ef4444' : 'text.primary',
                                                        }}
                                                    >
                                                        {formatDateForLocale(period.dueDate)}
                                                    </TableCell>
                                                    <TableCell onClick={(e) => e.stopPropagation()}>
                                                        <TargetAmountCell period={period} onUpdate={updateTargetAmount} readonly={isReadonlyTarget} />
                                                    </TableCell>
                                                    <TableCell
                                                        sx={{
                                                            fontFamily: 'monospace',
                                                            fontWeight: 700,
                                                            color: period.totalPaid > 0 ? '#10b981' : 'text.secondary',
                                                        }}
                                                    >
                                                        {formatCurrency(period.totalPaid, period.currency)}
                                                    </TableCell>
                                                    <TableCell>
                                                        <StatusChip status={period.status} targetAmount={period.targetAmount} totalPaid={period.totalPaid} />
                                                    </TableCell>
                                                    <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                                                        <Button
                                                            variant="outlined"
                                                            size="small"
                                                            startIcon={<AddIcon />}
                                                            onClick={() => handleOpenPaymentDialog(period)}
                                                            sx={{
                                                                borderRadius: 0,
                                                                borderWidth: '2px',
                                                                fontWeight: 700,
                                                                textTransform: 'uppercase',
                                                                fontSize: '0.625rem',
                                                            }}
                                                        >
                                                            Record
                                                        </Button>
                                                    </TableCell>
                                                </TableRow>
                                                {isExpanded && propertyId && (
                                                    <TableRow key={`${period.id}-breakdown`}>
                                                        <TableCell colSpan={7} sx={{ p: 0, borderBottom: '1px solid #374151', backgroundColor: 'rgba(99, 102, 241, 0.03)' }}>
                                                            <Collapse in={true}>
                                                                <TourismTaxBreakdownPanel propertyId={propertyId} periodId={period.id} />
                                                            </Collapse>
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                                </Fragment>
                                            );
                                        })
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </>
            )}

            <RecordPaymentDialog
                open={paymentDialogOpen}
                onClose={handleClosePaymentDialog}
                period={selectedPeriod}
                propertyId={propertyId}
                onSave={async (data: import('../hooks/useTaxPayments').CreatePaymentData) => {
                    await createPayment(data);
                    await refetch();
                }}
                onDelete={async (paymentId: number) => {
                    await deletePayment(paymentId);
                    await refetch();
                }}
            />

            <GeneratePeriodsDialog
                open={generateDialogOpen}
                onClose={() => setGenerateDialogOpen(false)}
                onGenerate={handleGeneratePeriods}
                currentYear={currentYear}
                mode={generateMode === 'hidden' ? 'generate' : generateMode}
            />

            <UploadInvoicesDialog
                open={uploadDialogOpen}
                onClose={() => {
                    setUploadDialogOpen(false);
                    refetch();
                }}
            />
        </PageContainer>
    );
};

export default TaxPaymentsPage;
